<section class="pdc" data-testid="documents-card" aria-labelledby="pdc-heading">
  <h3 id="pdc-heading" class="pdc__title">
    {{ 'documents.title' | translate }}
    @if (!loading() && !loadError()) {
      <span class="pdc__count">{{ documents().length }}</span>
    }
  </h3>

  @if (loading()) {
    <div class="pdc__loading">
      <mat-spinner diameter="28" />
    </div>
  } @else if (loadError()) {
    <p class="pdc__hint pdc__hint--error" role="alert">{{ 'documents.errors.load' | translate }}</p>
  } @else {
    <ul class="pdc__list">
      @for (document of documents(); track document.id) {
        <li class="pdc__item" [attr.data-testid]="'document-row-' + document.id">
          <span class="pdc__icon" [class.pdc__icon--reference]="isReference(document)" aria-hidden="true">
            <mat-icon>{{ iconFor(document) }}</mat-icon>
          </span>
          <span class="pdc__body">
            <span class="pdc__name">{{ document.name }}</span>
            @if (isReference(document)) {
              <code class="pdc__path">{{ document.externalRef }}</code>
            } @else {
              <span class="pdc__meta">{{ document.originalFilename }} · {{ formatSize(document.sizeBytes) }}</span>
            }
          </span>
          @if (isReference(document)) {
            <button
              type="button"
              class="pdc__action"
              [attr.data-testid]="'document-copy-path-' + document.id"
              [attr.aria-label]="('documents.copyPath' | translate) + ' ' + document.name"
              (click)="copyPath(document)"
            >
              <mat-icon aria-hidden="true">{{ copiedId() === document.id ? 'check' : 'content_copy' }}</mat-icon>
            </button>
          } @else {
            <button
              type="button"
              class="pdc__action"
              [attr.data-testid]="'document-download-' + document.id"
              [attr.aria-label]="('documents.download' | translate) + ' ' + document.name"
              [disabled]="busy()"
              (click)="download(document)"
            >
              <mat-icon aria-hidden="true">download</mat-icon>
            </button>
          }
          @if (canWrite()) {
            <button
              type="button"
              class="pdc__action pdc__action--remove"
              [attr.data-testid]="'document-unlink-' + document.id"
              [attr.aria-label]="('documents.unlink' | translate) + ' ' + document.name"
              [disabled]="busy()"
              (click)="unlink(document)"
            >
              <mat-icon aria-hidden="true">close</mat-icon>
            </button>
          }
        </li>
      } @empty {
        <li class="pdc__empty">{{ 'documents.empty' | translate }}</li>
      }
    </ul>

    @if (busy()) {
      <div class="pdc__progress" role="progressbar" [attr.aria-label]="'documents.busy' | translate"></div>
    }

    @if (canWrite()) {
      @if (uploadOpen()) {
        <app-file-dropzone
          [hint]="'documents.dropzoneHint' | translate: { max: maxMegabytes }"
          [disabled]="busy()"
          (fileSelected)="onFileSelected($event)"
        />
      }
      <button
        type="button"
        class="pdc__add"
        data-testid="documents-upload"
        [disabled]="busy()"
        (click)="uploadOpen.set(!uploadOpen())"
      >
        <mat-icon aria-hidden="true">upload_file</mat-icon>
        {{ 'documents.upload' | translate }}
      </button>
      <button
        type="button"
        class="pdc__add"
        data-testid="documents-link-existing"
        [disabled]="busy()"
        (click)="openLinkDialog()"
      >
        <mat-icon aria-hidden="true">add_link</mat-icon>
        {{ 'documents.linkExisting' | translate }}
      </button>
    }

    @if (errorKey(); as key) {
      <p class="pdc__hint pdc__hint--error" role="alert">
        {{ key | translate: { max: maxMegabytes, name: errorName() } }}
      </p>
    }
  }
</section>

<lib-modal
  [closeLabel]="'general.close' | translate"
  [visible]="linkDialogVisible()"
  [title]="'documents.dialog.title' | translate"
  [contentTemplate]="resolvedTemplate ?? undefined"
  [saveText]="'documents.dialog.confirm' | translate"
  [cancelText]="'documents.dialog.cancel' | translate"
  [disabled]="!selectedId() || busy()"
  (save)="confirmLink()"
  (cancel)="closeLinkDialog()"
  (visibleChange)="linkDialogVisible.set($event)"
></lib-modal>

<ng-template #linkDialog>
  <div class="pdc-dialog" data-testid="documents-link-dialog">
    <input
      type="search"
      class="pdc-dialog__search"
      [attr.aria-label]="'documents.dialog.search' | translate"
      [placeholder]="'documents.dialog.search' | translate"
      [value]="search()"
      (input)="search.set($any($event.target).value)"
    />
    @if (projectDocuments() === null) {
      <div class="pdc__loading"><mat-spinner diameter="24" /></div>
    } @else {
      <ul class="pdc-dialog__list" role="listbox" [attr.aria-label]="'documents.dialog.title' | translate">
        @for (document of dialogDocuments(); track document.id) {
          <li role="presentation">
            <button
              type="button"
              role="option"
              class="pdc-dialog__option"
              [class.pdc-dialog__option--selected]="selectedId() === document.id"
              [attr.aria-selected]="selectedId() === document.id"
              [disabled]="isLinked(document.id)"
              (click)="select(document.id)"
            >
              <mat-icon class="pdc-dialog__icon" aria-hidden="true">
                {{ isLinked(document.id) ? 'check' : iconFor(document) }}
              </mat-icon>
              <span class="pdc__body">
                <span class="pdc__name">{{ document.name }}</span>
                <span class="pdc__meta">
                  @if (isReference(document)) {
                    {{ document.externalRef }}
                  } @else {
                    {{ document.originalFilename }} · {{ formatSize(document.sizeBytes) }}
                  }
                  · {{ 'documents.dialog.linkCount' | translate: { count: document.linkCount } }}
                </span>
              </span>
            </button>
          </li>
        } @empty {
          <li class="pdc__empty">{{ 'documents.dialog.empty' | translate }}</li>
        }
      </ul>
    }
  </div>
</ng-template>
